import React, {useEffect, useMemo, useState} from 'react';
import {
    Alert,
    Button,
    Grid,
    Group,
    Paper,
    Stack,
    Text,
    Textarea,
    TextInput,
    Title,
} from '@mantine/core';
import {useDebouncedValue} from '@mantine/hooks';
import {notifications} from '@mantine/notifications';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {
    RegistrationMailService,
    type RegistrationMailTemplate,
} from '../../client';
import {formatRequestError} from '../../utils/formatRequestError';
import {RegistrationMailVariables} from './RegistrationMailVariables';
import {RegistrationMailAttachments} from './RegistrationMailAttachments';

const EMPTY_TEMPLATE: RegistrationMailTemplate = {
    subject: '',
    message: '',
    footer: '',
    attachments: [],
};

export function RegistrationMailEditor() {
    const queryClient = useQueryClient();
    const {data: savedTemplate, isLoading} = useQuery({
        queryKey: ['registration-mail-template'],
        queryFn: RegistrationMailService.getRegistrationMailTemplateApiV1RegistrationMailTemplateGet,
    });

    const [template, setTemplate] = useState<RegistrationMailTemplate>(EMPTY_TEMPLATE);
    const [savedSnapshot, setSavedSnapshot] = useState<RegistrationMailTemplate>(EMPTY_TEMPLATE);
    const [testEmail, setTestEmail] = useState('');
    const [saving, setSaving] = useState(false);
    const [sendingTest, setSendingTest] = useState(false);
    const [preview, setPreview] = useState<{subject: string; body: string} | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    const [debouncedTemplate] = useDebouncedValue(template, 400);

    useEffect(() => {
        if (savedTemplate) {
            setTemplate({
                ...savedTemplate,
                attachments: savedTemplate.attachments ?? [],
            });
            setSavedSnapshot({
                ...savedTemplate,
                attachments: savedTemplate.attachments ?? [],
            });
        }
    }, [savedTemplate]);

    const isDirty = useMemo(() => {
        const textFields = (value: RegistrationMailTemplate) => ({
            subject: value.subject,
            message: value.message,
            footer: value.footer,
        });
        return JSON.stringify(textFields(template)) !== JSON.stringify(textFields(savedSnapshot));
    }, [template, savedSnapshot]);

    useEffect(() => {
        if (!debouncedTemplate.subject && !debouncedTemplate.message && !debouncedTemplate.footer) {
            return;
        }
        setPreviewLoading(true);
        RegistrationMailService.previewRegistrationMailTemplateApiV1RegistrationMailPreviewPost({
            requestBody: debouncedTemplate,
        })
            .then((result) => setPreview(result))
            .catch((error: unknown) => {
                setPreview(null);
                notifications.show({
                    color: 'red',
                    title: 'Could not render preview',
                    message: formatRequestError(error, 'Request failed'),
                });
            })
            .finally(() => setPreviewLoading(false));
    }, [debouncedTemplate]);

    const updateField = (field: keyof RegistrationMailTemplate, value: string) => {
        setTemplate((current) => ({...current, [field]: value}));
    };

    const saveTemplate = () => {
        setSaving(true);
        RegistrationMailService.saveRegistrationMailTemplateApiV1RegistrationMailTemplatePut({
            requestBody: template,
        })
            .then((result) => {
                setSavedSnapshot(result);
                setTemplate(result);
                queryClient.setQueryData(['registration-mail-template'], result);
                notifications.show({
                    color: 'green',
                    title: 'Template saved',
                    message: 'The registration mail template has been saved.',
                });
            })
            .catch((error: unknown) => {
                notifications.show({
                    color: 'red',
                    title: 'Could not save template',
                    message: formatRequestError(error, 'Request failed'),
                });
            })
            .finally(() => setSaving(false));
    };

    const sendTestMail = () => {
        if (!testEmail) {
            return;
        }
        setSendingTest(true);
        RegistrationMailService.sendRegistrationMailTestApiV1RegistrationMailTestPost({
            requestBody: {...template, email: testEmail},
        })
            .then(() => {
                notifications.show({
                    color: 'green',
                    title: 'Test mail sent',
                    message: `A test mail was sent to ${testEmail}.`,
                });
            })
            .catch((error: unknown) => {
                notifications.show({
                    color: 'red',
                    title: 'Could not send test mail',
                    message: formatRequestError(error, 'Request failed'),
                });
            })
            .finally(() => setSendingTest(false));
    };

    return (
        <Stack gap="lg">
            {isDirty ? (
                <Alert color="yellow" title="Unsaved changes">
                    You have unsaved changes. Save the template before sending to all registrations.
                </Alert>
            ) : null}
            <Grid>
                <Grid.Col span={{base: 12, md: 6}}>
                    <Stack gap="sm">
                        <TextInput
                            label="Subject"
                            placeholder="Mail subject"
                            value={template.subject}
                            onChange={(event) => updateField('subject', event.currentTarget.value)}
                            disabled={isLoading}
                        />
                        <Textarea
                            label="Message"
                            placeholder="Mail message"
                            minRows={14}
                            autosize
                            value={template.message}
                            onChange={(event) => updateField('message', event.currentTarget.value)}
                            disabled={isLoading}
                        />
                        <Textarea
                            label="Footer"
                            placeholder="Footer (appended after --)"
                            minRows={4}
                            autosize
                            value={template.footer}
                            onChange={(event) => updateField('footer', event.currentTarget.value)}
                            disabled={isLoading}
                        />
                        <Group gap="xs">
                            <Button loading={saving} onClick={saveTemplate} disabled={isLoading}>
                                Save template
                            </Button>
                        </Group>
                        <TextInput
                            label="Test email address"
                            placeholder="you@example.com"
                            value={testEmail}
                            onChange={(event) => setTestEmail(event.currentTarget.value)}
                        />
                        <Button
                            variant="outline"
                            loading={sendingTest}
                            onClick={sendTestMail}
                            disabled={!testEmail || isLoading}
                        >
                            Send test mail
                        </Button>
                        <RegistrationMailAttachments
                            attachments={template.attachments ?? []}
                            disabled={isLoading}
                            onAttachmentsChange={(result) => {
                                setTemplate((current) => ({
                                    ...current,
                                    attachments: result.attachments ?? [],
                                }));
                                setSavedSnapshot((current) => ({
                                    ...current,
                                    attachments: result.attachments ?? [],
                                }));
                            }}
                        />
                    </Stack>
                </Grid.Col>
                <Grid.Col span={{base: 12, md: 6}}>
                    <Paper withBorder p="md" radius="md" h="100%">
                        <Stack gap="sm">
                            <Title order={5}>Preview</Title>
                            {previewLoading ? (
                                <Text size="sm" c="dimmed">Rendering preview…</Text>
                            ) : preview ? (
                                <>
                                    <Text size="sm" fw={600}>Subject</Text>
                                    <Text size="sm">{preview.subject}</Text>
                                    <Text size="sm" fw={600} mt="sm">Body</Text>
                                    <Text
                                        size="sm"
                                        component="pre"
                                        style={{whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0}}
                                    >
                                        {preview.body}
                                    </Text>
                                </>
                            ) : (
                                <Text size="sm" c="dimmed">Enter content to see a preview.</Text>
                            )}
                        </Stack>
                    </Paper>
                </Grid.Col>
            </Grid>
            <RegistrationMailVariables />
        </Stack>
    );
}
